import { getBulkUploadDownloadLink } from '@disco/utils/upload'
import debounce from 'lodash/debounce'

import React from 'react'
import { Link } from 'react-router-dom'
import type { UploadProvider } from 'types/upload'
import { Button, Flex, Link as RadixLink, Separator, Text } from 'components/ds'
import UploadFileOrFolderButtons from './upload-buttons/upload-file-or-folder-button'
import UploadProviderButtons from './upload-buttons/upload-provider-buttons'
type Props = {
  onPickedFiles: (files: any[], provider: UploadProvider) => void
  showDivider?: boolean
  importFileText?: string
  onSkip?: () => void
}
/**
 * UploadOptions component for displaying file upload methods
 *
 * A functional component that provides multiple file upload options including
 * local file/folder selection, cloud provider imports, and bulk upload tools.
 * Features debounced file handling and optional skip functionality.
 *
 * @param props - Component props
 * @param props.onPickedFiles - Callback function called when files are selected
 * @param props.showDivider - Whether to show visual divider between sections
 * @param props.importFileText - Custom text for import section
 * @param props.onSkip - Optional callback for skip functionality
 * @returns JSX element containing the complete upload options interface
 */
export default function UploadOptions({
  onPickedFiles,
  importFileText,
  showDivider,
  onSkip,
}: Props): React.ReactNode {
  const handlePickedFiles = debounce(
    (files: File[], provider: UploadProvider) => {
      if (!files) return
      onPickedFiles(files, provider)
    },
    200,
    {
      leading: true,
    },
  )
  return (
    <Flex direction="column" gap="4" align="center">
      <UploadFileOrFolderButtons onFilesSelect={handlePickedFiles} />
      {showDivider && <Separator size="4" />}
      <Text align="center" size="2">
        {importFileText ? (
          <span>{importFileText}</span>
        ) : (
          <span>Import from:</span>
        )}
      </Text>
      <UploadProviderButtons onFilesSelect={handlePickedFiles} />

      {onSkip && (
        <>
          <Separator size="4" />
          <Text align="center" size="3">
            <Button variant="soft" onClick={onSkip}>
              Skip this, I’ll upload files later.
            </Button>
          </Text>
        </>
      )}

      <Text align="center" size="2">
        Uploading hundreds of files? Use our{' '}
        <RadixLink {...getBulkUploadDownloadLink()}>Bulk Uploader</RadixLink>
      </Text>
    </Flex>
  )
}
